class SoldOutLast {
	static availabilityParameter = "filter.v.availability";

	static requestId = 0;

	static getBaseSearchParams() {
		const searchParams = new URLSearchParams(window.location.search);
		searchParams.delete("page");
		searchParams.delete("section_id");
		searchParams.delete(this.availabilityParameter);
		return searchParams;
	}

	static getProductPageUrl(sectionId, availability, page) {
		const searchParams = this.getBaseSearchParams();
		searchParams.set("section_id", sectionId);
		searchParams.set(this.availabilityParameter, availability);
		searchParams.set("page", page);

		return `${window.location.pathname}?${searchParams.toString()}`;
	}

	static async fetchProductPage(sectionId, availability, page) {
		const response = await fetch(
			this.getProductPageUrl(sectionId, availability, page),
		);

		if (!response.ok) {
			throw new Error(`Unable to load collection page (${response.status})`);
		}

		const document = new DOMParser().parseFromString(
			await response.text(),
			"text/html",
		);

		return Array.from(
			document.querySelectorAll("#product-grid > .collection-grid-card"),
		);
	}

	static getSourcePages(start, end, pageSize) {
		if (start >= end) return [];

		const firstPage = Math.floor(start / pageSize) + 1;
		const lastPage = Math.floor((end - 1) / pageSize) + 1;

		return Array.from(
			{ length: lastPage - firstPage + 1 },
			(_, index) => firstPage + index,
		);
	}

	static async getProducts(
		sectionId,
		availability,
		start,
		end,
		pageSize,
	) {
		const sourcePages = this.getSourcePages(start, end, pageSize);
		if (sourcePages.length === 0) return [];

		const pageProducts = await Promise.all(
			sourcePages.map((page) =>
				this.fetchProductPage(sectionId, availability, page),
			),
		);
		const products = pageProducts.flat();
		const firstPageOffset = (sourcePages[0] - 1) * pageSize;

		return products.slice(start - firstPageOffset, end - firstPageOffset);
	}

	static hasExplicitAvailabilityFilter() {
		return new URLSearchParams(window.location.search).has(
			this.availabilityParameter,
		);
	}

	static async render() {
		const currentRequestId = ++this.requestId;
		const productGridContainer = document.getElementById(
			"ProductGridContainer",
		);
		const productGrid = productGridContainer?.querySelector("#product-grid");
		const collection = productGridContainer?.querySelector(".collection-product");
		const revealCollection = () => {
			collection?.classList.remove("loading", "sold-out-last-loading");
		};

		if (
			!productGridContainer ||
			!productGrid ||
			productGridContainer.dataset.soldOutLast !== "true" ||
			this.hasExplicitAvailabilityFilter()
		) {
			revealCollection();
			return;
		}

		const pageSize = Number(productGridContainer.dataset.productsPerPage);
		const productsCount = Number(productGridContainer.dataset.productsCount);
		const availableProductsCount = Number(
			productGridContainer.dataset.availableProductsCount,
		);
		const soldOutProductsCount = Number(
			productGridContainer.dataset.soldOutProductsCount,
		);

		if (
			!pageSize ||
			!productsCount ||
			productsCount !== availableProductsCount + soldOutProductsCount
		) {
			revealCollection();
			return;
		}

		const currentPage = Math.max(
			1,
			Number(new URLSearchParams(window.location.search).get("page")) || 1,
		);
		if (currentPage > Math.ceil(productsCount / pageSize)) {
			revealCollection();
			return;
		}

		const pageStart = (currentPage - 1) * pageSize;
		const pageEnd = Math.min(pageStart + pageSize, productsCount);
		const availableStart = Math.min(pageStart, availableProductsCount);
		const availableEnd = Math.min(pageEnd, availableProductsCount);
		const soldOutStart = Math.max(0, pageStart - availableProductsCount);
		const soldOutEnd = Math.max(0, pageEnd - availableProductsCount);
		const sectionId = productGrid.dataset.id;

		collection?.classList.add("loading");

		try {
			const [availableProducts, soldOutProducts] = await Promise.all([
				this.getProducts(
					sectionId,
					"1",
					availableStart,
					availableEnd,
					pageSize,
				),
				this.getProducts(
					sectionId,
					"0",
					soldOutStart,
					soldOutEnd,
					pageSize,
				),
			]);

			if (currentRequestId !== this.requestId) return;

			const products = [...availableProducts, ...soldOutProducts].map(
				(product) => document.importNode(product, true),
			);

			if (products.length !== pageEnd - pageStart) {
				throw new Error("The composed collection page is incomplete");
			}

			productGrid.replaceChildren(...products);

			if (typeof colorSwatches === "function") colorSwatches();
		} catch (error) {
			console.warn("Unable to move sold-out products to the end.", error);
		} finally {
			if (currentRequestId === this.requestId) {
				revealCollection();
			}
		}
	}
}

window.SoldOutLast = SoldOutLast;
SoldOutLast.render();
